import React, {useEffect, useRef, useState} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter, Button} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {SpinnerButton} from '../../SpinnerButton';
import {
    RELABEL_KINDS, PICK_PRIMARY_KIND, PICK_SPECIFIC_KINDS,
    kindLabelKey, kindDescKey, kindIcon,
} from './contextPackConstants';

// Elegir el tipo de material del Paquete de Contexto. Sirve a los dos momentos en que esa pregunta
// aparece, porque es la MISMA pregunta con la misma lista:
//
//  - CORREGIR (con `doc`): el tipo de un documento ya cargado. Existe porque el tipo se decide en la
//    ingesta y ahí se puede errar — el clasificador se equivoca, y todo lo subido antes de que la
//    carga aceptara declararlo quedó etiquetado por su origen. Ofrece los 9 tipos, incluida la
//    transcripción: corregir "esto en realidad era una reunión" es legítimo.
//  - ELEGIR (sin `doc`): qué se está por subir, desde el menú "+ Agregar". Ofrece los 8 declarables
//    —sin transcripción, que tiene su propia entrada en ese menú— y al confirmar el padre navega a
//    la carga con el tipo ya fijado. Ahí «Otro material» es la opción PRINCIPAL y viene elegida:
//    subir algo que no encaja en ninguna categoría es un click y no exige clasificar nada. Los
//    siete tipos específicos quedan abajo como sugerencias — declararlos le da contexto previo a
//    la IA, pero es opcional. Estaba al revés, y en el piloto la conclusión fue que no se podían
//    subir documentos (card JH1GK3r9).
//
// `doc`, no `document`: ese nombre sombrea el global del navegador dentro de todo el componente.
export function DocumentKindModal({isOpen, doc, toggle, onConfirm, isSaving}) {
    const {t} = useTranslation();
    const [selected, setSelected] = useState(null);
    const isRelabel = !!doc;
    const optionsRef = useRef(null);

    // Al abrir, el foco va a la opción marcada: es un grupo de radios, así que desde ahí las flechas
    // ya mueven la selección. Reactstrap lo deja en el contenedor del modal y había que dar Tab para
    // entrar, con las flechas sin hacer nada (QA-alim-15).
    const focusSelected = () => {
        const radio = optionsRef.current && optionsRef.current.querySelector('input[type="radio"]:checked');
        if (radio) radio.focus();
    };

    // Al abrir, arranca en el tipo que el documento ya tiene (corregir) o en la opción principal
    // (elegir): el camino de «subo esto y listo» no puede empezar por una decisión.
    useEffect(() => {
        if (isOpen) setSelected(doc ? doc.document_kind : PICK_PRIMARY_KIND);
    }, [isOpen, doc]);

    // Corregir exige un cambio real; elegir se confirma con cualquier selección.
    const canConfirm = !!selected && (!isRelabel || selected !== doc.document_kind);

    const option = (kind) => (
        <li key={kind}>
            <label className={`cmem-kind-option ${selected === kind ? '-selected' : ''}`}>
                <input type="radio" name="cmem-doc-kind" value={kind}
                       checked={selected === kind}
                       onChange={() => setSelected(kind)}/>
                <span className="cmem-kind-option-icon" aria-hidden="true">
                    <i className={`fas fa-${kindIcon(kind)}`}/>
                </span>
                <span className="cmem-kind-option-text">
                    <span className="cmem-kind-option-title">
                        <strong>{t(kindLabelKey(kind))}</strong>
                        {isRelabel && doc.document_kind === kind && (
                            <span className="cmem-kind-current">
                                {t('client_memory.documents.kind_modal_current')}
                            </span>
                        )}
                    </span>
                    <span className="cmem-kind-option-desc">{t(kindDescKey(kind))}</span>
                </span>
            </label>
        </li>
    );
    const copyKey = (suffix) => (isRelabel
        ? `client_memory.documents.kind_modal_${suffix}`
        : `client_memory.documents.kind_pick_${suffix}`);

    return (
        <Modal isOpen={isOpen} toggle={toggle} onOpened={focusSelected}>
            <ModalHeader toggle={toggle}>{t(copyKey('title'))}</ModalHeader>
            <ModalBody>
                <div className="modal-section cmem-kind-modal" ref={optionsRef}>
                    <p className="cmem-kind-lead">{t(copyKey('lead'))}</p>
                    {isRelabel ? (
                        <ul className="cmem-kind-options" role="radiogroup"
                            aria-label={t(copyKey('title'))}>
                            {RELABEL_KINDS.map(option)}
                        </ul>
                    ) : (
                        <div role="radiogroup" aria-label={t(copyKey('title'))}>
                            <ul className="cmem-kind-options -primary">
                                {option(PICK_PRIMARY_KIND)}
                            </ul>
                            <p className="cmem-kind-specific-head">
                                <strong>{t('client_memory.documents.kind_pick_specific_title')}</strong>
                                <span>{t('client_memory.documents.kind_pick_specific_hint')}</span>
                            </p>
                            <ul className="cmem-kind-options">
                                {PICK_SPECIFIC_KINDS.map(option)}
                            </ul>
                        </div>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>{t('common.cancel')}</Button>
                {isSaving ? (
                    <SpinnerButton/>
                ) : (
                    <button type="button" className="btn -alt" disabled={!canConfirm}
                            onClick={() => onConfirm(selected)}>
                        {t(copyKey('confirm'))}
                    </button>
                )}
            </ModalFooter>
        </Modal>
    );
}
